import { useCallback } from 'react';
import { RbacPermissionRule } from 'api/rbac/rbac-types';
import { Sprint } from 'api/sprint-planning/types';
import {
  ProjectResource,
  ProjectResourceOrUndefined,
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { SprintType } from './types';
import { useSprintDetails } from './use-sprint-details';

export const useSprintAccess = () => {
  const { data: sprintDetails } = useSprintDetails();
  const { projects, sprintType } = sprintDetails ?? {};

  const { hasAccessToResource } = useWithAccess();

  const projectResource = useProjectAsRBACResource(projects?.[0]?.projectId);

  const isSingleProjectSprint = useCallback(
    (sprint) => {
      if (sprint) {
        return (
          sprint?.projects?.[0]?.projectId &&
          sprint.sprintType === SprintType.SingleProject
        );
      }

      return (
        projects?.[0]?.projectId && sprintType === SprintType.SingleProject
      );
    },
    [projects, sprintType],
  );

  const getProject = useCallback(
    (sprint) => {
      if (sprint) {
        return ProjectResourceOrUndefined(sprint.projects[0]);
      }

      return projectResource;
    },
    [projectResource],
  );

  const hasAccess = useCallback(
    (permission: 'WRITE' | 'DELETE') => (sprint?: Sprint) => {
      if (isSingleProjectSprint(sprint)) {
        return hasAccessToResource({
          rule: `SPRINT_PROJECT:${permission}` as RbacPermissionRule,
          resourceData: getProject(sprint) as ProjectResource,
        });
      }

      return hasAccessToResource({
        rule: `SPRINT:${permission}` as RbacPermissionRule,
      });
    },
    [isSingleProjectSprint, getProject, hasAccessToResource],
  );

  const hasWriteAccess = useCallback(
    (sprint?: Sprint) => hasAccess('WRITE')(sprint),
    [hasAccess],
  );

  const hasDeleteAccess = useCallback(
    (sprint?: Sprint) => hasAccess('DELETE')(sprint),
    [hasAccess],
  );

  return { hasWriteAccess, hasDeleteAccess };
};
